import api from './client';

interface UploadResponse {
  status: number;
  message?: string;
  error?: string | string[];
  url?: string;
  urls?: string[];
}

/**
 * POST /api/uploads/listing-photos
 * Sends one or more File objects as multipart/form-data under the field
 * name "photos" and returns the absolute URLs the backend assigned.
 */
export const uploadListingPhotos = async (files: File[]): Promise<string[]> => {
  if (!files.length) return [];
  const form = new FormData();
  for (const f of files) {
    form.append('photos', f);
  }

  const res = await api.post<UploadResponse>('/uploads/listing-photos', form, {
    headers: {
      // axios will set the multipart boundary itself when we hand it FormData;
      // we just have to make sure the default 'application/json' header from
      // the client doesn't override it.
      'Content-Type': 'multipart/form-data',
    },
    timeout: 60_000, // larger images can take longer than the default 15s
  });

  if (res.data.status !== 1 || !res.data.urls) {
    const err = res.data.error;
    const msg = Array.isArray(err) ? err.join(', ') : err || 'Upload failed';
    throw new Error(msg);
  }
  return res.data.urls;
};

/**
 * POST /api/uploads/category-images
 * Sends a single File as multipart/form-data under the field name "image"
 * and returns the absolute URL to store on the category.
 */
export const uploadCategoryImage = async (file: File): Promise<string> => {
  const form = new FormData();
  form.append('image', file);

  const res = await api.post<UploadResponse>('/uploads/category-images', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60_000,
  });

  if (res.data.status !== 1 || !res.data.url) {
    const err = res.data.error;
    const msg = Array.isArray(err) ? err.join(', ') : err || 'Upload failed';
    throw new Error(msg);
  }
  return res.data.url;
};
